import { Button, buttonVariants } from "@car/ui/components/button";
import { Skeleton } from "@car/ui/components/skeleton";
import { cn } from "@car/ui/lib/utils";
import { Call02Icon, CarAlertIcon, Message01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { openConsult } from "@/lib/consult";
import { BranchCard } from "@/components/branch-card";
import { MobileNavigation, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { loadSiteContent, telHref, useSiteContent } from "@/lib/content";
import { toFaDigits } from "@/lib/format";
import { pageCrumbs, pageMeta, pageTitle } from "@/lib/seo";
import { orpc } from "@/utils/orpc";

import { publicPageHeaders } from "./-public-cache";

export const Route = createFileRoute("/contact")({
  // already in the cache from the root route; here for the page's title
  loader: ({ context }) => loadSiteContent(context.queryClient),
  head: () => ({
    meta: [
      ...pageMeta(
        pageTitle("تماس با ما"),
        "تلفن فروش، امداد جاده‌ای، نشانی، ساعت کاری و مسیریابی شعبه‌های تابان خودرو.",
      ),
      pageCrumbs("تماس با ما", "/contact"),
    ],
  }),
  headers: publicPageHeaders,
  component: ContactPage,
});

const cardClass = "grid content-start gap-2 rounded-2xl border bg-card p-5";

/** تلفن فروش و امداد از بلوک «تماس» و شعب فعال (نشانی، ساعت‌ها، تلفن، مسیریابی) در یک صفحه */
function ContactPage() {
  const { contact } = useSiteContent();
  const branches = useQuery(orpc.site.branches.queryOptions());

  return (
    <main className="min-h-[100dvh] bg-background pb-20 text-foreground md:pb-0">
      <SiteHeader />
      <header className="border-b bg-muted/45">
        <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
          <nav aria-label="مسیر صفحه" className="text-sm text-muted-foreground">
            <a href="/" className="hover:text-foreground">
              خانه
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span aria-current="page" className="font-bold text-foreground">
              تماس با ما
            </span>
          </nav>
          <h1 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl">تماس با ما</h1>
          <p className="mt-3 max-w-[60ch] leading-7 text-pretty text-muted-foreground">
            برای خرید و مشاوره با فروش تماس بگیرید، شماره‌تان را بگذارید تا کارشناس تماس بگیرد یا به
            نزدیک‌ترین شعبه سر بزنید.
          </p>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-10 sm:px-6 sm:py-14">
        <section aria-label="راه‌های تماس" className="grid gap-4 md:grid-cols-3">
          <div className={cardClass}>
            <h2 className="flex items-center gap-2 text-lg font-black">
              <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} className="size-5 text-primary" />
              فروش و مشاوره خرید
            </h2>
            <p className="text-sm text-muted-foreground">{contact.salesHours}</p>
            {contact.salesPhone ? (
              <a
                href={telHref(contact.salesPhone)}
                className={cn(buttonVariants({ size: "lg" }), "mt-2 justify-self-start")}
              >
                <span dir="ltr">{toFaDigits(contact.salesPhone)}</span>
              </a>
            ) : (
              <p className="text-sm leading-6">با تلفن هر شعبه (پایین همین صفحه) تماس بگیرید.</p>
            )}
          </div>
          <div className={cardClass}>
            <h2 className="flex items-center gap-2 text-lg font-black">
              <HugeiconsIcon
                icon={Message01Icon}
                strokeWidth={1.8}
                className="size-5 text-primary"
              />
              درخواست تماس
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              نام و شماره‌تان را بگذارید تا کارشناس فروش با شما تماس بگیرد.
            </p>
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={openConsult}
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "mt-2 justify-self-start",
              )}
            >
              فرم درخواست مشاوره
            </button>
          </div>
          {contact.hotlinePhone ? (
            <div className={cardClass}>
              <h2 className="flex items-center gap-2 text-lg font-black">
                <HugeiconsIcon
                  icon={CarAlertIcon}
                  strokeWidth={1.8}
                  className="size-5 text-destructive"
                />
                {contact.hotlineLabel}
              </h2>
              <p className="text-sm leading-6 text-muted-foreground">
                خرابی یا حادثه در راه، شبانه‌روز؛ برای خرید با تلفن فروش تماس بگیرید.
              </p>
              <a
                href={telHref(contact.hotlinePhone)}
                className={cn(
                  buttonVariants({ variant: "destructive", size: "lg" }),
                  "mt-2 justify-self-start",
                )}
              >
                <span dir="ltr">{toFaDigits(contact.hotlinePhone)}</span>
              </a>
            </div>
          ) : null}
        </section>

        <section aria-labelledby="contact-branches">
          <h2 id="contact-branches" className="text-2xl font-black">
            شعبه‌ها
          </h2>
          <div className="home-branch-list mt-4" aria-live="polite">
            {branches.isPending ? (
              [0, 1].map((key) => <Skeleton key={key} className="my-6 h-40 rounded-2xl" />)
            ) : branches.isError ? (
              <div className="grid justify-items-start gap-3 py-6">
                <p>فهرست شعبه‌ها بارگذاری نشد.</p>
                <Button variant="outline" size="lg" onClick={() => branches.refetch()}>
                  تلاش دوباره
                </Button>
              </div>
            ) : branches.data.branches.length === 0 ? (
              <p className="py-6 text-muted-foreground">
                فعلاً شعبه‌ای برای نمایش نیست؛ از فرم درخواست مشاوره شماره‌تان را بگذارید.
              </p>
            ) : (
              branches.data.branches.map((branch) => <BranchCard key={branch.id} branch={branch} />)
            )}
          </div>
        </section>
      </div>
      <SiteFooter />
      <MobileNavigation />
    </main>
  );
}
